import { supportsNormalizedTemplate } from '../helpers/patterns/patternCoordinates';
import { MEASUREMENT_VISUALS, getReportMeasurementContext } from '../helpers/reports/reportMeasurementContext';
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Card, Space, Spin, Alert, Typography, Tag, DatePicker, Select, Row, Col } from 'antd';
import axios from 'axios';
import dayjs, { Dayjs } from 'dayjs';
import { measurementDateKey } from '../utils/measurementTime';
import { ENDPOINTS } from '../helpers/api/apiConfig';
import { trackActivity } from '../helpers/analytics/trackActivity';
import {
    ReportKpiCards,
    ReportBenchmarkCards,
    ReportBenchmarkRadar,
    ReportTable,
    ReportLineChart,
    ReportBarChart,
    ReportPatternPointLineChart,
    ReportHeatmapSection,
    ReportAreaHierarchy,
    ReportAnimatedHeatmap,
    ReportAnimatedPatternPoints,
    ReportPatternList,
    ReportWarningCard,
    ReportAvailabilityCalendar,
    ReportMeasurementList,
    ReportMeasurementInfo,
    ReportMeasurementValues,
    ReportMeasurementTexts,
    ReportMeasurementWeather,
    ReportMeasurementLocationMap,
    ReportMeasurementPatternLocation,
    ReportMeasurementFiles,
    ReportLatestAnalysis,
    ReportDailyKpiHistory,
} from './ReportSections';
import { MeasurementReportProvider } from './ReportSections/measurementReport/MeasurementReportContext';



const { Text } = Typography;

interface ReportProps {
    template: any;                  // Template JSON from backend
    areaId?: number;                // Optional: initial area to show report for
    dateFrom?: Date | string;       // Optional: initial date range
    dateTo?: Date | string;         // Optional: initial date range
    patternId?: number;             // Optional: pattern context for pattern-based sections
    patternPointId?: number;        // Optional: pattern point context for pattern-based sections
    measurementId?: number;         // Optional: measurement context for measurement sections
    showControls?: boolean;         // Whether to show area/date picker controls
    lockScope?: boolean;            // When true, lock area/date scope changes
    onAreaClick?: (areaId: number, rangeOverride?: [Dayjs, Dayjs]) => void; // Callback when area link is clicked
    onMetricClick?: (metricKey: string, metricId: number | undefined, areaId: number) => void; // Callback when metric link is clicked
    onPatternClick?: (patternId: number, areaId: number) => void; // Callback when pattern link is clicked
    onPatternPointClick?: (patternPointId: number, patternId: number, areaId: number) => void; // Callback when pattern point link is clicked
    onMeasurementClick?: (measurementId: number, areaId?: number | null) => void; // Callback when measurement link is clicked
    onDateRangeChange?: (range: [Dayjs, Dayjs]) => void; // Optional callback for date range changes
}

interface CommonSectionProps {
    section: any;
    data: any[];
    areas: any[];
    areaTypes: any[];
    metrics: any[];
    previewAreaId: number;
    dateRange?: [Dayjs, Dayjs];
    patternId?: number;
    patternPointId?: number | null;
    measurementId?: number | null;
    onAreaClick?: (areaId: number, rangeOverride?: [Dayjs, Dayjs]) => void;
    onMetricClick?: (metricKey: string, metricId: number | undefined, areaId: number) => void;
    onPatternClick?: (patternId: number, areaId: number) => void;
    onPatternPointClick?: (patternPointId: number, patternId: number, areaId: number) => void;
    onMeasurementClick?: (measurementId: number, areaId?: number | null) => void;
}

export const Report: React.FC<ReportProps> = ({
    template,
    areaId: initialAreaId,
    dateFrom: initialDateFrom,
    dateTo: initialDateTo,
    patternId,
    patternPointId,
    measurementId,
    showControls = true,
    lockScope = false,
    onAreaClick,
    onMetricClick,
    onPatternClick,
    onPatternPointClick,
    onMeasurementClick,
    onDateRangeChange,
}) => {
    const [loading, setLoading] = useState(true);
    const [loadingData, setLoadingData] = useState(false);
    const [error, setError] = useState<string | null>(null);
    
    // Meta data
    const [areas, setAreas] = useState<any[]>([]);
    const [areaTypes, setAreaTypes] = useState<any[]>([]);
    const [metrics, setMetrics] = useState<any[]>([]);
    const [measurementOptions, setMeasurementOptions] = useState<any[]>([]);
    const [loadingMeasurements, setLoadingMeasurements] = useState(false);
    
    // Report parameters
    const [selectedAreaId, setSelectedAreaId] = useState<number | null>(initialAreaId || null);
    
    // Read lookbackDays from template root or requiredInputs
    const lookbackDays = template?.lookbackDays 
        ?? template?.requiredInputs?.lookbackDays?.default 
        ?? 30;
    
    const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
        dayjs(initialDateFrom || new Date()).subtract(lookbackDays - 1, 'day'),
        dayjs(initialDateTo || new Date()),
    ]);
    
    // Section data
    const [sectionData, setSectionData] = useState<Record<string, any>>({});

    // Sync date range with props when they change
    useEffect(() => {
        if (initialDateFrom && initialDateTo) {
            setDateRange([dayjs(initialDateFrom), dayjs(initialDateTo)]);
        }
    }, [initialDateFrom, initialDateTo]);

    // Sync area with prop when it changes
    useEffect(() => {
        if (initialAreaId !== undefined && initialAreaId !== null) {
            setSelectedAreaId(initialAreaId);
        }
    }, [initialAreaId]);

    // Load meta data on mount
    useEffect(() => {
        const loadMeta = async () => {
            setLoading(true);
            setError(null);
            const isMeasurementOnly = getReportMeasurementContext(template, measurementId).isMeasurementOnly;

            // Measurement sections fetch their own scoped data through the
            // MeasurementReportProvider and must not depend on metric metadata.
            if (isMeasurementOnly) {
                setAreas([]);
                setAreaTypes([]);
                setMetrics([]);
                setLoading(false);
                return;
            }

            try {
                const [areasRes, areaTypesRes, metaRes] = await Promise.all([
                    axios.get(ENDPOINTS.AREAS_V2, { withCredentials: true, params: { include_archived: 'true' } }),
                    axios.get(ENDPOINTS.AREA_TYPES_V2, { withCredentials: true }),
                    axios.get(ENDPOINTS.REPORT_TEMPLATE_WIZARD_META, { withCredentials: true }),
                ]);
                
                setAreas(areasRes.data?.data || areasRes.data || []);
                
                const areaTypesData = areaTypesRes.data?.data || areaTypesRes.data || [];
                console.log('📦 [Report.tsx] Area Types API Response:', {
                    total: areaTypesData.length,
                    firstAreaType: areaTypesData[0],
                    hasTemplateImage: areaTypesData.some((at: any) => at.templateImage),
                    hasTemplateLinesImage: areaTypesData.some((at: any) => at.templateLinesImage),
                    areaType9: areaTypesData.find((at: any) => at.id === 9),
                    areaType1: areaTypesData.find((at: any) => at.id === 1),
                });
                
                setAreaTypes(areaTypesData);
                const metaData = metaRes.data?.data || metaRes.data || {};
                setMetrics(metaData.metrics || []);
            } catch (err) {
                console.error('Failed to load meta data:', err);
                setError('Failed to load required data. Please refresh the page.');
            } finally {
                setLoading(false);
            }
        };
        loadMeta();
    }, [template, measurementId]);

    // Collect area IDs based on template settings
    const collectAreaIds = useCallback(
        (rootId: number | null) => {
            if (!rootId || !template) return [];
            
            // Read from template root or fallback to requiredInputs
            const includeDesc = template.includeDescendants 
                ?? template.requiredInputs?.includeDescendants?.default 
                ?? true;
            const maxDepth = template.maxDepth 
                ?? template.requiredInputs?.maxDepth?.default 
                ?? 0;
            
            const map: Record<number, { children: number[] }> = {};
            areas.forEach((a: any) => {
                if (!map[a.id]) map[a.id] = { children: [] };
                if (a.parentAreaId) {
                    if (!map[a.parentAreaId]) map[a.parentAreaId] = { children: [] };
                    map[a.parentAreaId].children.push(a.id);
                }
            });
            
            const result: number[] = [];
            const queue: Array<{ id: number; depth: number }> = [{ id: rootId, depth: 0 }];
            
            while (queue.length) {
                const { id, depth } = queue.shift()!;
                result.push(id);
                if (includeDesc && depth < maxDepth) {
                    const children = map[id]?.children || [];
                    children.forEach((childId) => 
                        queue.push({ id: childId, depth: depth + 1 })
                    );
                }
            }
            return result;
        },
        [areas, template]
    );

    const collectDescendantAreaIds = useCallback(
        (rootId: number | null) => {
            if (!rootId) return [];

            const childrenMap = new Map<number, number[]>();
            areas.forEach((area: any) => {
                if (!area?.id || !area?.parentAreaId) return;
                const current = childrenMap.get(area.parentAreaId) || [];
                current.push(area.id);
                childrenMap.set(area.parentAreaId, current);
            });

            const result: number[] = [];
            const queue = [...(childrenMap.get(rootId) || [])];
            while (queue.length > 0) {
                const areaId = queue.shift()!;
                result.push(areaId);
                const next = childrenMap.get(areaId) || [];
                next.forEach((childId) => queue.push(childId));
            }
            return result;
        },
        [areas]
    );

    // Collect full hierarchy (root + all descendants) for map context
    const collectHierarchyAreaIds = useCallback(
        (areaId: number | null) => {
            if (!areaId) return [];

            const selectedArea = areas.find((a: any) => a.id === areaId);
            if (!selectedArea) return [areaId];

            let currentId: number = areaId;
            let rootId: number = areaId;
            while (currentId) {
                const area = areas.find((a: any) => a.id === currentId);
                if (!area || area.parentAreaId == null) {
                    rootId = currentId;
                    break;
                }
                currentId = area.parentAreaId;
                rootId = area.parentAreaId;
            }

            const collected = new Set<number>();
            const queue = [rootId];
            while (queue.length) {
                const id = queue.shift()!;
                collected.add(id);
                areas.forEach((a: any) => {
                    if (a.parentAreaId === id) {
                        queue.push(a.id);
                    }
                });
            }

            return Array.from(collected);
        },
        [areas]
    );

    // Create stable date range key for dependency tracking
    const dateRangeKey = `${dateRange[0].format('YYYY-MM-DD')}_${dateRange[1].format('YYYY-MM-DD')}`;

    const { hasMeasurementSections, resolvedMeasurementId, analysisUsesMeasurement } = useMemo(
        () => getReportMeasurementContext(template, measurementId),
        [template, measurementId]
    );
    const [selectedMeasurementId, setSelectedMeasurementId] = useState<number | null>(resolvedMeasurementId);

    useEffect(() => {
        if (resolvedMeasurementId && resolvedMeasurementId !== selectedMeasurementId) {
            setSelectedMeasurementId(resolvedMeasurementId);
        }
    }, [resolvedMeasurementId, selectedMeasurementId]);

    useEffect(() => {
        if (!hasMeasurementSections) {
            setSelectedMeasurementId(null);
            setMeasurementOptions([]);
            return;
        }
        setLoadingMeasurements(true);
        axios
            .get(ENDPOINTS.MEASUREMENTS_V2, {
                params: {
                    area_id: selectedAreaId ?? undefined,
                    limit: 200,
                    offset: 0,
                },
                withCredentials: true,
            })
            .then((res) => {
                const rows = res.data?.data || [];
                setMeasurementOptions(rows);
            })
            .catch((err) => {
                console.warn('[Report] Failed to load measurements', err);
                setMeasurementOptions([]);
            })
            .finally(() => {
                setLoadingMeasurements(false);
            });
    }, [hasMeasurementSections, selectedAreaId]);

    useEffect(() => {
        if (!hasMeasurementSections) return;
        if (measurementOptions.length === 0) {
            if (!resolvedMeasurementId) {
                setSelectedMeasurementId(null);
            }
            return;
        }
        const exists = selectedMeasurementId
            ? measurementOptions.some((m: any) => m.id === selectedMeasurementId)
            : false;
        if (!exists && !resolvedMeasurementId) {
            setSelectedMeasurementId(measurementOptions[0].id);
        }
    }, [measurementOptions, selectedMeasurementId, hasMeasurementSections, resolvedMeasurementId]);

    useEffect(() => {
        if (!hasMeasurementSections || !resolvedMeasurementId) return;
        const alreadyIncluded = measurementOptions.some((m: any) => m.id === resolvedMeasurementId);
        if (alreadyIncluded) return;

        let cancelled = false;
        axios
            .get(ENDPOINTS.MEASUREMENT_DETAIL(resolvedMeasurementId), { withCredentials: true })
            .then((res) => {
                if (cancelled) return;
                const data = res.data?.data || res.data;
                if (!data?.id) return;
                setMeasurementOptions((prev) => {
                    if (prev.some((m: any) => m.id === data.id)) return prev;
                    return [data, ...prev];
                });
            })
            .catch((err) => {
                if (cancelled) return;
                console.warn('[Report] Failed to load selected measurement', err);
            });

        return () => {
            cancelled = true;
        };
    }, [hasMeasurementSections, resolvedMeasurementId, measurementOptions]);

    const templatePatterns = useMemo(
        () => (Array.isArray(template?.patterns) ? template.patterns : []),
        [template]
    );
    const templatePatternPoints = useMemo(
        () => (Array.isArray(template?.patternPoints) ? template.patternPoints : []),
        [template]
    );
    const patternPointById = useMemo(() => {
        const map = new Map<number, any>();
        templatePatternPoints.forEach((entry: any) => {
            const patternPoint = entry?.patternPoint ?? entry;
            const id = patternPoint?.id ?? entry?.patternPointId ?? entry?.id;
            if (id) {
                map.set(id, patternPoint);
            }
        });
        return map;
    }, [templatePatternPoints]);
    const defaultPatternId = useMemo(() => {
        const ids = templatePatterns
            .map((p: any) => p?.pattern?.id ?? p?.patternId ?? p?.id)
            .filter((id: any) => typeof id === 'number' && !Number.isNaN(id));
        return ids.length === 1 ? ids[0] : undefined;
    }, [templatePatterns]);
    const defaultPatternPointId = useMemo(() => {
        const ids = templatePatternPoints
            .map((p: any) => p?.patternPoint?.id ?? p?.patternPointId ?? p?.id)
            .filter((id: any) => typeof id === 'number' && !Number.isNaN(id));
        return ids.length === 1 ? ids[0] : undefined;
    }, [templatePatternPoints]);
    const resolvePatternContext = useCallback((section: any) => {
        const effectivePatternPointId =
            section?.patternPointId ?? patternPointId ?? defaultPatternPointId;
        const patternPoint = effectivePatternPointId
            ? patternPointById.get(effectivePatternPointId)
            : undefined;
        const patternIdFromPoint = patternPoint?.patternId ?? patternPoint?.pattern?.id;
        const effectivePatternId =
            section?.patternId ?? patternIdFromPoint ?? patternId ?? defaultPatternId;

        return { patternId: effectivePatternId, patternPointId: effectivePatternPointId };
    }, [defaultPatternId, defaultPatternPointId, patternId, patternPointById, patternPointId]);

    // Helper: Build heatmap payload with mode detection (used by both heatmap and animated-heatmap)
    const buildHeatmapPayload = useCallback((
        basePayload: any,
        section: any,
        selectedAreaId: number
    ) => {
        const area = areas.find((a: any) => a.id === selectedAreaId);
        const areaType = areaTypes.find((at: any) => at.id === (area?.areaType?.id ?? area?.areaTypeId));
        // The backend picks a single coordinate frame from all samples in the range.
        const payload: any = { ...basePayload, gridResolutionX: section.heatmapGridResolutionX || 30,
            hexagonSizeMeters: section.hexagonSizeMeters || 15 };
        const dims = areaType?.templateDimensions;
        if (dims) {
            const width = Number(dims.widthMeters ?? dims.width);
            const height = Number(dims.heightMeters ?? dims.height);
            if (Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0) {
                payload.templateSize = { width, height, widthMeters: width, heightMeters: height };
            }
        }
        if (section.heatmapGridResolutionY) payload.gridResolutionY = section.heatmapGridResolutionY;
        return { payload, isTemplateMode: supportsNormalizedTemplate(areaType) };
    }, [areas, areaTypes]);

    // Load section data when area or date range changes
    useEffect(() => {
        // Guard: ensure all required data is loaded before making API calls
        if (!selectedAreaId || !template || !template.sections || loading) {
            return;
        }
        
        // Guard: ensure areas and areaTypes are loaded (prevents race condition)
        if (areas.length === 0 || areaTypes.length === 0) {
            console.log('⏳ Waiting for areas/areaTypes to load before fetching section data');
            return;
        }

        const abortController = new AbortController();
        
        const loadSectionData = async () => {
            setLoadingData(true);
            setError(null);
            
            try {
                const promises = template.sections.map(async (section: any) => {
                    // Skip heatmap sections - they now fetch their own data per-area
                    if (section.visual === 'heatmap') {
                        return { key: section.key, data: [] };
                    }
                    if (MEASUREMENT_VISUALS.includes(section.visual) || section.visual === 'latest-analysis') {
                        return { key: section.key, data: [] };
                    }
                    
                    let targetAreaIds = section.visual === 'area-hierarchy'
                        ? collectHierarchyAreaIds(selectedAreaId)
                        : section.visual === 'animated-heatmap'
                            ? [selectedAreaId]
                            : (section.includeDescendants !== false
                                ? collectAreaIds(selectedAreaId)
                                : [selectedAreaId]);

                    if (section.visual === 'benchmark-radar') {
                        const compareChildren = section.showChildren !== false;
                        if (compareChildren) {
                            const childAreaIds = collectDescendantAreaIds(selectedAreaId);
                            targetAreaIds = childAreaIds.length > 0 ? childAreaIds : [selectedAreaId];
                        } else {
                            targetAreaIds = [selectedAreaId];
                        }
                    }
                    if (section.visual === 'benchmark-cards' && section.showParent === false) {
                        const childAreaIds = collectDescendantAreaIds(selectedAreaId);
                        targetAreaIds = childAreaIds.length > 0 ? childAreaIds : [selectedAreaId];
                    }
                    if (section.visual === 'daily-kpi-history') {
                        const includeParent = section.showParent !== false;
                        const includeChildren = section.showChildren === true;
                        const childAreaIds = collectDescendantAreaIds(selectedAreaId);
                        if (includeParent && includeChildren) {
                            targetAreaIds = [selectedAreaId, ...childAreaIds];
                        } else if (includeParent) {
                            targetAreaIds = [selectedAreaId];
                        } else if (includeChildren) {
                            targetAreaIds = childAreaIds.length > 0 ? childAreaIds : [selectedAreaId];
                        } else {
                            targetAreaIds = [selectedAreaId];
                        }
                    }
                    
                    const basePayload: any = {
                        areaIds: targetAreaIds.length ? targetAreaIds : [selectedAreaId],
                    };
                    if (section.visual !== 'calendar-availability') {
                        basePayload.dateRange = {
                            from: dateRange[0].toISOString(),
                            to: dateRange[1].toISOString(),
                        };
                    }
                    // Pattern list doesn't need metrics
                    if (!['pattern-list', 'area-hierarchy', 'calendar-availability', 'measurement-list', ...MEASUREMENT_VISUALS].includes(section.visual) && (!section.metrics || section.metrics.length === 0)) {
                        return { key: section.key, data: [] };
                    }
                    
                    let endpoint = ENDPOINTS.REPORT_SECTION_KPI;
                    const payload: any = { ...basePayload };
                    const { patternId: resolvedPatternId, patternPointId: resolvedPatternPointId } =
                        resolvePatternContext(section);
                    
                    // Only add metrics if not pattern-list or area-hierarchy
                    if (!['pattern-list', 'area-hierarchy', 'calendar-availability'].includes(section.visual)) {
                        payload.metrics = section.metrics;
                    }
                    
                    if (section.visual === 'table') {
                        endpoint = ENDPOINTS.REPORT_SECTION_TABLE;
                        payload.groupBy = section.groupBy || 'childArea';
                        // Pass fields configuration if specified
                        if (section.fields && section.fields.length > 0) {
                            payload.fields = section.fields;
                        }
                    } else if (section.visual === 'daily-kpi-history') {
                        endpoint = ENDPOINTS.REPORT_SECTION_LINE;
                        payload.resolution = 'day';
                    } else if (section.visual === 'line' || section.visual === 'bar') {
                        endpoint = ENDPOINTS.REPORT_SECTION_LINE;
                        payload.resolution = section.resolution || 'day';
                    } else if (section.visual === 'pattern-point-line') {
                        endpoint = ENDPOINTS.REPORT_SECTION_PATTERN_POINT_LINE;
                        payload.resolution = section.resolution || 'day';
                        if (!resolvedPatternId) {
                            console.warn('[Report] Missing patternId for pattern-point-line section:', section.key);
                            return { key: section.key, data: [] };
                        }
                        payload.patternId = resolvedPatternId;
                        if (resolvedPatternPointId !== undefined && resolvedPatternPointId !== null) {
                            payload.patternPointId = resolvedPatternPointId;
                        }
                    } else if (section.visual === 'area-hierarchy') {
                        endpoint = ENDPOINTS.REPORT_SECTION_AREA_HIERARCHY;
                    } else if (section.visual === 'animated-heatmap') {
                        endpoint = ENDPOINTS.REPORT_SECTION_ANIMATED_HEATMAP;
                        
                        // Use helper to build heatmap payload with mode detection
                        const result = buildHeatmapPayload(basePayload, section, selectedAreaId);
                        Object.assign(payload, result.payload);
                        payload.resolution = section.resolution || 'day';
                        payload.responseShape = 'frames';
                    } else if (section.visual === 'pattern-list') {
                        endpoint = ENDPOINTS.REPORT_SECTION_PATTERN_LIST;
                        // Pattern list uses base payload (areaIds + dateRange)
                    } else if (section.visual === 'calendar-availability') {
                        endpoint = ENDPOINTS.REPORT_SECTION_CALENDAR_AVAILABILITY;
                    } else if (section.visual === 'measurement-list') {
                        endpoint = ENDPOINTS.REPORT_SECTION_MEASUREMENT_LIST;
                        if (resolvedPatternPointId !== undefined && resolvedPatternPointId !== null) {
                            payload.patternPointId = resolvedPatternPointId;
                        } else if (resolvedPatternId) {
                            payload.patternId = resolvedPatternId;
                        }
                    }
                    
                    const res = await axios.post(endpoint, payload, {
                        withCredentials: true,
                        signal: abortController.signal,
                    });
                    
                    const data = res.data?.data || res.data || [];
                    const patternPoints = res.data?.patternPoints || null;
                    
                    if (section.visual === 'heatmap') {
                        console.log('📍 [NORMAL HEATMAP] AFTER API - Response:', {
                            dataLength: data.length,
                            firstItem: data[0],
                            firstItemGrid: data[0]?.grid
                        });
                    } else if (section.visual === 'animated-heatmap') {
                        console.log('🎯 [ANIMATED HEATMAP] AFTER API - Response:', {
                            dataLength: data.length,
                            firstItem: data[0],
                            firstItemGrid: data[0]?.grid
                        });
                    }
                    
                    return { key: section.key, data, patternPoints };
                });

                const results = await Promise.all(promises);
                const map: Record<string, any> = {};
                results.forEach((r) => {
                    if (r) {
                        map[r.key] = r.data;
                        // Store pattern points metadata if provided
                        if (r.patternPoints) {
                            map[`${r.key}_patternPoints`] = r.patternPoints;
                        }
                    }
                });
                
                setSectionData(map);
            } catch (err: any) {
                if (axios.isCancel(err) || err.name === 'CanceledError') {
                    return; // User changed parameters, ignore
                }
                console.error('Failed to load report data:', err);
                setError('Failed to load report data. Please try again.');
            } finally {
                if (!abortController.signal.aborted) {
                    setLoadingData(false);
                }
            }
        };

        loadSectionData();
        
        return () => {
            abortController.abort();
        };
    }, [selectedAreaId, dateRangeKey, template, loading, collectAreaIds, collectHierarchyAreaIds, collectDescendantAreaIds, areas, areaTypes, resolvePatternContext]);

    // Set initial area if not provided
    useEffect(() => {
        // Read areaTypeIds from template root or metadata
        const areaTypeIds = template?.areaTypeIds ?? template?.metadata?.appliesToAreaTypes ?? [];
        
        if (!selectedAreaId && areas.length > 0 && areaTypeIds?.length > 0) {
            // Find first area matching template's area types
            const matchingArea = areas.find((a: any) => 
                areaTypeIds.includes(a.areaType?.id)
            );
            if (matchingArea) {
                setSelectedAreaId(matchingArea.id);
            } else if (areas.length > 0) {
                setSelectedAreaId(areas[0].id);
            }
        }
    }, [selectedAreaId, areas, template]);

    // Filter areas by template area types
    const filteredAreas = React.useMemo(() => {
        const areaTypeIds = template?.areaTypeIds ?? template?.metadata?.appliesToAreaTypes ?? [];
        
        if (!areaTypeIds || areaTypeIds.length === 0) {
            return areas;
        }
        return areas.filter((a: any) => areaTypeIds.includes(a.areaType?.id));
    }, [areas, template]);

    // Render section based on visual type
    const renderSection = (section: any) => {
        if (!selectedAreaId && !MEASUREMENT_VISUALS.includes(section.visual)
            && !(section.visual === 'latest-analysis' && analysisUsesMeasurement)) {
            return <Alert message="Please select an area" type="info" />;
        }

        const dataKey = section.key || section.id;
        const data = sectionData[dataKey] || [];
        const patternContext = resolvePatternContext(section);
        
        const commonProps: CommonSectionProps = {
            section,
            data,
            areas,
            areaTypes,
            metrics,
            previewAreaId: selectedAreaId as number,
            dateRange,
            patternId: patternContext.patternId,
            patternPointId: patternContext.patternPointId ?? null,
            measurementId: hasMeasurementSections ? selectedMeasurementId ?? resolvedMeasurementId ?? null : null,
            onAreaClick,
            onMetricClick,
            onPatternClick,
            onPatternPointClick,
            onMeasurementClick,
        };

        switch (section.visual) {
            case 'kpi-cards':
                return <ReportKpiCards {...commonProps} />;
            case 'benchmark-cards':
                return <ReportBenchmarkCards {...commonProps} />;
            case 'benchmark-radar':
                return <ReportBenchmarkRadar {...commonProps} />;
            case 'table':
                return <ReportTable {...commonProps} />;
            case 'line':
                // Build metric labels and area names for line chart
                const metricLabels: Record<string, string> = {};
                metrics.forEach((m: any) => {
                    metricLabels[m.key] = m.name || m.key;
                });
                
                const areaNames: Record<number, string> = {};
                areas.forEach((a: any) => {
                    if (a.id && a.name) {
                        areaNames[a.id] = a.name;
                    }
                });
                
                return (
                    <ReportLineChart
                        title={section.title}
                        data={data}
                        fields={section.fields}
                        metricLabels={metricLabels}
                        metrics={metrics}
                        areaNames={areaNames}
                        showParent={section.showParent !== false}
                        showChildren={section.showChildren === true}
                        onAreaClick={onAreaClick}
                    />
                );
            case 'bar':
                // Build metric labels and area names for bar chart
                const barMetricLabels: Record<string, string> = {};
                metrics.forEach((m: any) => {
                    barMetricLabels[m.key] = m.name || m.key;
                });

                const barAreaNames: Record<number, string> = {};
                areas.forEach((a: any) => {
                    if (a.id && a.name) {
                        barAreaNames[a.id] = a.name;
                    }
                });

                return (
                    <ReportBarChart
                        title={section.title}
                        data={data}
                        fields={section.fields}
                        metricLabels={barMetricLabels}
                        metrics={metrics}
                        areaNames={barAreaNames}
                        showParent={section.showParent !== false}
                        showChildren={section.showChildren === true}
                        previewAreaId={selectedAreaId as number}
                        onAreaClick={onAreaClick}
                    />
                );
            case 'daily-kpi-history':
                return <ReportDailyKpiHistory {...commonProps} />;
            case 'pattern-point-line':
                // Build metric labels for pattern point line chart
                const ppMetricLabels: Record<string, string> = {};
                metrics.forEach((m: any) => {
                    ppMetricLabels[m.key] = m.name || m.key;
                });
                
                // Pattern points - get from API response stored in sectionData
                const patternPoints = sectionData[`${dataKey}_patternPoints`] || [];
                const effectivePatternPointId = patternContext.patternPointId ?? null;
                const filteredPatternPoints = effectivePatternPointId
                    ? patternPoints.filter((pp: any) => pp.id === effectivePatternPointId)
                    : patternPoints;
                const filteredData = effectivePatternPointId
                    ? (Array.isArray(data) ? data.map((metricEntry: any) => ({
                        ...metricEntry,
                        points: Array.isArray(metricEntry?.points)
                            ? metricEntry.points.filter((p: any) => p.patternPointId === effectivePatternPointId)
                            : [],
                    })) : [])
                    : data;
                const showParent = effectivePatternPointId ? false : section.showParent !== false;
                const showPoints = effectivePatternPointId ? true : section.showPoints !== false;
                
                // Debug log for showPoints configuration
                console.log('🔧 PatternPointLine Section Config:', {
                    showParent: section.showParent,
                    showParentResult: showParent,
                    showPoints: section.showPoints,
                    showPointsOld: section.showPoints === true,
                    showPointsNew: showPoints,
                    patternPointsCount: filteredPatternPoints.length,
                    patternPointId: effectivePatternPointId,
                });
                
                return (
                    <ReportPatternPointLineChart
                        title={section.title}
                        data={filteredData}
                        fields={section.fields}
                        metricLabels={ppMetricLabels}
                        metrics={metrics}
                        patternPoints={filteredPatternPoints}
                        showParent={showParent}
                        showPoints={showPoints}
                        previewAreaId={selectedAreaId as number}
                        onPatternPointClick={onPatternPointClick}
                        onMetricClick={onMetricClick}
                    />
                );
            case 'heatmap':
                return <ReportHeatmapSection 
                    key={`heatmap-${selectedAreaId}`} 
                    {...commonProps}
                    dateRange={{
                        from: dateRange[0].toISOString(),
                        to: dateRange[1].toISOString(),
                    }}
                />;
            case 'animated-heatmap':
                return <ReportAnimatedHeatmap key={`animated-${selectedAreaId}`} {...commonProps} />;
            case 'animated-pattern-points':
                return (
                    <ReportAnimatedPatternPoints
                        key={`animated-pattern-${selectedAreaId}`}
                        {...commonProps}
                        dateRange={dateRange}
                    />
                );
            case 'area-hierarchy':
                const hierarchyAreaIds = collectHierarchyAreaIds(selectedAreaId);
                const hierarchyAreas = areas.filter((a: any) => hierarchyAreaIds.includes(a.id));
                const hierarchyMeasurements = sectionData[dataKey] || [];
                
                // Build areaTypeTemplates map
                const areaTypeTemplatesMap = new Map<number, string>();
                areaTypes.forEach((at: any) => {
                    if ((at as any).templateImage || (at as any).hasTemplate) {
                        areaTypeTemplatesMap.set(at.id, `${ENDPOINTS.AREA_TYPES_V2}/${at.id}/template-image?t=${Date.now()}`);
                    }
                });
                
                return (
                    <ReportAreaHierarchy
                        areas={hierarchyAreas}
                        areaTypeTemplates={areaTypeTemplatesMap}
                        title={section.title || 'Area Hierarchy'}
                        showMap={section.showMap !== false}
                        selectedAreaId={selectedAreaId}
                        measurements={hierarchyMeasurements}
                        onAreaClick={onAreaClick}
                        onMeasurementClick={onMeasurementClick}
                    />
                );
            case 'pattern-list':
                return <ReportPatternList {...commonProps} />;
            case 'calendar-availability':
                return (
                    <ReportAvailabilityCalendar
                        section={section}
                        data={data}
                        areaName={filteredAreas.find((a: any) => a.id === selectedAreaId)?.name || 'No area'}
                        dateRange={dateRange}
                        onDateRangeChange={
                            lockScope
                                ? undefined
                                : (range) => {
                                      setDateRange(range);
                                      if (onDateRangeChange) {
                                          onDateRangeChange(range);
                                      }
                                  }
                        }
                        disableDateChange={lockScope}
                    />
                );
            case 'measurement-list':
                return <ReportMeasurementList {...commonProps} />;
            case 'measurement-info':
                return <ReportMeasurementInfo section={section} />;
            case 'measurement-values':
                return <ReportMeasurementValues section={section} />;
            case 'measurement-texts':
                return <ReportMeasurementTexts section={section} />;
            case 'measurement-weather':
                return <ReportMeasurementWeather section={section} />;
            case 'measurement-location':
                return <ReportMeasurementLocationMap section={section} />;
            case 'measurement-pattern-location':
                return <ReportMeasurementPatternLocation section={section} />;
            case 'measurement-files':
                return <ReportMeasurementFiles section={section} />;
            case 'latest-analysis':
                return <ReportLatestAnalysis {...commonProps} measurementId={analysisUsesMeasurement ? commonProps.measurementId : null} />;
            case 'warning-card':
                return <ReportWarningCard {...commonProps} />;
            default:
                return <Text type="secondary">Unknown section type: {section.visual}</Text>;
        }
    };

    const renderSectionGrid = () => (
        <Row gutter={[16, 16]}>
            {template.sections.map((section: any) => {
                const span = section.span ?? 24; // Default to full width
                const content = renderSection(section);
                const visualLabelMap: Record<string, string> = {
                    'kpi-cards': 'Summary',
                    'benchmark-cards': 'Benchmark',
                    'benchmark-radar': 'Benchmark radar',
                    table: 'Table',
                    line: 'Trend',
                    bar: 'Bar chart',
                    'daily-kpi-history': 'History',
                    'pattern-point-line': 'Pattern trend',
                    heatmap: 'Heatmap',
                    'animated-heatmap': 'Animated heatmap',
                    'animated-pattern-points': 'Animated points',
                    'pattern-list': 'Pattern list',
                    'calendar-availability': 'Calendar',
                    'measurement-list': 'Measurements',
                    'measurement-info': 'Measurement info',
                    'measurement-values': 'Values',
                    'measurement-texts': 'Texts',
                    'measurement-weather': 'Weather',
                    'measurement-location': 'Location',
                    'measurement-pattern-location': 'Pattern location',
                    'measurement-files': 'Files',
                    'latest-analysis': 'Latest analysis',
                };
                const visualLabel = visualLabelMap[section.visual] || String(section.visual || 'Section');
                const visualClass = `report-section-visual-${String(section.visual || 'unknown')
                    .toLowerCase()
                    .replace(/[^a-z0-9-]/g, '-')}`;
                if (
                    section.visual === 'heatmap' ||
                    section.visual === 'warning-card' ||
                    section.visual === 'area-hierarchy' ||
                    section.visual === 'animated-pattern-points' ||
                    section.visual === 'benchmark-cards' ||
                    section.visual === 'benchmark-radar' ||
                    section.visual === 'calendar-availability' ||
                    section.visual === 'table' ||
                    section.visual === 'kpi-cards' ||
                    section.visual === 'line' ||
                    section.visual === 'pattern-point-line' ||
                    section.visual === 'bar' ||
                    section.visual === 'latest-analysis' ||
                    section.visual === 'measurement-list' ||
                    section.visual === 'pattern-list' ||
                    section.visual === 'animated-heatmap' ||
                    MEASUREMENT_VISUALS.includes(section.visual)
                ) {
                    if (!content) return null;
                    return (
                        <Col key={section.key} xs={24} sm={24} md={span} lg={span} xl={span} xxl={span}>
                            <div className={`report-section-visual ${visualClass}`}>{content}</div>
                        </Col>
                    );
                }
                return (
                    <Col key={section.key} xs={24} sm={24} md={span} lg={span} xl={span} xxl={span}>
                        <Card
                            size="small"
                            className={`report-section-card report-section-visual ${visualClass}`}
                            title={<Text strong>{section.title || section.key}</Text>}
                            extra={
                                <Text type="secondary">
                                    {visualLabel} · {filteredAreas.find((a: any) => a.id === selectedAreaId)?.name || 'No area'} ·{' '}
                                    {dateRange[0].format('YYYY-MM-DD')} → {dateRange[1].format('YYYY-MM-DD')}
                                </Text>
                            }
                        >
                            {content}
                        </Card>
                    </Col>
                );
            })}
        </Row>
    );

    if (loading) {
        return (
            <div style={{ padding: 24, textAlign: 'center' }}>
                <Spin size="large" tip="Loading report..." />
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ padding: 24 }}>
                <Alert message="Error" description={error} type="error" showIcon />
            </div>
        );
    }

    if (!template) {
        return (
            <div style={{ padding: 24 }}>
                <Alert 
                    message="No Template" 
                    description="No report template provided" 
                    type="warning" 
                    showIcon 
                />
            </div>
        );
    }

    return (
        <div className="report-content">
            <Space direction="vertical" style={{ width: '100%' }} size="large">
                {/* Report Header - Simple title without Card */}
                <Row gutter={[16, 16]} align="middle">
                    <Col flex="auto">
                        <Typography.Title level={3} style={{ margin: 0 }}>
                            {template.name || 'Report'}
                        </Typography.Title>
                        {template.description && (
                            <Text type="secondary">{template.description}</Text>
                        )}
                    </Col>
                    {showControls && (
                        <Col>
                            <Space size="large" wrap className="report-controls">
                                <div className="report-control">
                                    <Text strong>Area: </Text>
                                    <Select
                                        style={{ width: 220 }}
                                        placeholder="Select area"
                                        value={selectedAreaId ?? undefined}
                                        disabled={lockScope}
                                        onChange={(val) => {
                                            setSelectedAreaId(val);
                                            trackActivity({
                                                action: 'select',
                                                entityType: 'area',
                                                entityId: val,
                                                areaId: val,
                                                reportTemplateId: template?.id,
                                                dateFrom: dateRange[0].toDate(),
                                                dateTo: dateRange[1].toDate(),
                                                route: window.location.pathname,
                                            });
                                            if (onAreaClick && !lockScope) {
                                                onAreaClick(val, dateRange);
                                            }
                                        }}
                                        options={filteredAreas.map((a: any) => ({
                                            label: a.name,
                                            value: a.id,
                                        }))}
                                        showSearch
                                        optionFilterProp="label"
                                    />
                                </div>
                                {hasMeasurementSections && (
                                    <div className="report-control">
                                        <Text strong>Measurement: </Text>
                                        <Select
                                            style={{ width: 260 }}
                                            placeholder="Select measurement"
                                            value={selectedMeasurementId ?? undefined}
                                            onChange={(val) => setSelectedMeasurementId(val)}
                                            loading={loadingMeasurements}
                                            options={measurementOptions.map((m: any) => ({
                                                label: `${m.measurementCode || `Measurement ${m.id}`} · ${m.measuredAt ? measurementDateKey(m.measuredAt, m) : new Date(m.createdAt).toISOString().slice(0, 10)}`,
                                                value: m.id,
                                            }))}
                                            showSearch
                                            optionFilterProp="label"
                                            allowClear
                                        />
                                    </div>
                                )}
                                <div className="report-control">
                                    <Text strong>Date Range: </Text>
                                    <DatePicker.RangePicker
                                        value={dateRange}
                                        disabled={lockScope}
                                        onChange={(range) => {
                                            if (lockScope) return;
                                            if (range && range[0] && range[1]) {
                                                setDateRange([range[0], range[1]]);
                                                trackActivity({
                                                    action: 'select',
                                                    entityType: 'report',
                                                    entityId: template?.id,
                                                    reportTemplateId: template?.id,
                                                    areaId: selectedAreaId ?? null,
                                                    dateFrom: range[0].toDate(),
                                                    dateTo: range[1].toDate(),
                                                    route: window.location.pathname,
                                                });
                                                if (onDateRangeChange) {
                                                    onDateRangeChange([range[0], range[1]]);
                                                }
                                            }
                                        }}
                                    />
                                </div>
                            </Space>
                        </Col>
                    )}
                </Row>

                {/* Loading State */}
                {loadingData && (
                    <div className="report-loading-surface">
                        <div className="report-loading-content">
                            <Spin tip="Loading report data..." />
                        </div>
                    </div>
                )}

                {/* Render Sections */}
                {!loadingData && template.sections && template.sections.length > 0 ? (
                    hasMeasurementSections ? (
                        <MeasurementReportProvider measurementId={selectedMeasurementId}>
                            {renderSectionGrid()}
                        </MeasurementReportProvider>
                    ) : (
                        renderSectionGrid()
                    )
                ) : (
                    !loadingData && (
                        <Alert
                            message="No Sections"
                            description="This report template has no sections defined"
                            type="info"
                            showIcon
                        />
                    )
                )}
            </Space>
        </div>
    );
};

export default React.memo(Report);
